import React, { useState } from 'react';
import { ActionItem, actionsStore } from '../../services/actionsStore';
import { Badge } from '../ui/Badge';
import { User, Briefcase, Eye, Edit, Trash2, CheckCircle2, Circle } from 'lucide-react';
import { Tooltip } from '../ui/Tooltip';
import { useNotifications } from '../../contexts/NotificationContext';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { DatePicker } from '../ui/DatePicker';
import { Textarea } from '../ui/Textarea';
import { Button } from '../ui/Button';
interface ActionsTableProps {
  actions: ActionItem[];
  showClient?: boolean;
  showResponsible?: boolean;
  onActionUpdate?: () => void;
}
export function ActionsTable({
  actions,
  showClient = false,
  showResponsible = false,
  onActionUpdate
}: ActionsTableProps) {
  const {
    addNotification
  } = useNotifications();
  const [selectedAction, setSelectedAction] = useState<ActionItem | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const handleToggleDone = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    actionsStore.toggleDone(id);
    if (onActionUpdate) onActionUpdate();
  };
  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (window.confirm('Sei sicuro di voler eliminare questa azione?')) {
      actionsStore.delete(id);
      addNotification('success', 'Azione eliminata', "L'azione è stata eliminata con successo.");
      if (onActionUpdate) onActionUpdate();
    }
  };
  const handleView = (e: React.MouseEvent, action: ActionItem) => {
    e.stopPropagation();
    setSelectedAction(action);
    setViewModalOpen(true);
  };
  const handleEdit = (e: React.MouseEvent, action: ActionItem) => {
    e.stopPropagation();
    setSelectedAction(action);
    setEditModalOpen(true);
  };
  const handleSaveEdit = () => {
    setEditModalOpen(false);
    addNotification('success', 'Azione aggiornata', 'Le modifiche sono state salvate con successo.');
    if (onActionUpdate) onActionUpdate();
  };
  const formatDate = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };
  if (actions.length === 0) {
    return <div className="text-center py-8 text-slate-400" data-id="element-241" data-mp="1i8qkbutfzgv7">
        <p className="text-sm" data-id="element-242" data-mp="1d9nqz7mtqz32">Nessuna azione trovata</p>
      </div>;
  }
  return <>
      <div className="overflow-x-auto" data-id="element-243" data-mp="1r12bjiwdr8c8">
        <table className="w-full text-left border-collapse" data-id="element-244" data-mp="1r23o5rt1o2w2">
          <thead data-id="element-245" data-mp="1m9ai90luaoie">
            <tr className="bg-slate-50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold sticky top-0 z-10" data-id="element-246" data-mp="1m8ra2blzrehk">
              <th className="px-4 py-3 bg-slate-50" data-id="element-247" data-mp="yhved3eezz51">Azione</th>
              <th className="px-4 py-3 bg-slate-50" data-id="element-248" data-mp="uhuvn6ka91s6">Assegnatario</th>
              {showClient && <th className="px-4 py-3 bg-slate-50" data-id="element-249" data-mp="kdoage2smg0e">Azienda</th>}
              <th className="px-4 py-3 bg-slate-50" data-id="element-250" data-mp="p560w876wy74">Attività</th>
              <th className="px-4 py-3 bg-slate-50" data-id="element-251" data-mp="p6qwm158qn6k">Avanzamento</th>
              <th className="px-4 py-3 bg-slate-50" data-id="element-252" data-mp="ahkxseabeyz7">Scadenza</th>
              {showResponsible && <th className="px-4 py-3 bg-slate-50" data-id="element-253" data-mp="fgo21wih1164">Responsabile</th>}
              <th className="px-4 py-3 bg-slate-50 text-right" data-id="element-254" data-mp="134xn2w7v1kw7">Azioni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-255" data-mp="1838xp95zbzfh">
            {actions.map(action => {
            const isOverdue = !action.isDone && new Date(action.deadline) < new Date();
            return <tr key={action.id} className={`group transition-colors ${action.isDone ? 'bg-slate-50/50 opacity-75' : isOverdue ? 'bg-red-50/30 hover:bg-red-50/60' : 'hover:bg-slate-50'}`} data-id="element-256" data-mp="y49lxhpx3no9">
                  <td className="px-4 py-3" data-id="element-257" data-mp="2ylqeewuee2j">
                    <span className={`text-sm font-medium ${action.isDone ? 'text-slate-500 line-through' : 'text-slate-900'}`} data-id="element-258" data-mp="2zn12i9y4yjq">
                      {action.name}
                    </span>
                  </td>
                  <td className="px-4 py-3" data-id="element-259" data-mp="3kf3kxogkhpwh">
                    <div className="flex items-center gap-2" data-id="element-260" data-mp="3kgeq9spnv5pp">
                      {action.assigneeType === 'consulente' ? <User className="h-4 w-4 text-seform-orange flex-shrink-0" data-id="element-261" data-mp="gxv5tcd8545g" /> : <Briefcase className="h-4 w-4 text-blue-500 flex-shrink-0" data-id="element-262" data-mp="byi97zwjwipo" />}
                      <span className={`text-sm ${action.isDone ? 'text-slate-500' : 'text-slate-700'}`} data-id="element-263" data-mp="7070jshzse91">
                        {action.assigneeName}
                      </span>
                    </div>
                  </td>
                  {showClient && <td className="px-4 py-3" data-id="element-264" data-mp="178n8ultv74jv">
                      <span className={`text-sm ${action.isDone ? 'text-slate-500' : 'text-slate-700'}`} data-id="element-265" data-mp="175so0t6xvhpx">
                        {action.clientName}
                      </span>
                    </td>}
                  <td className="px-4 py-3" data-id="element-266" data-mp="1lxjn50mildsx">
                    <Badge variant="neutral" size="sm" className={action.isDone ? 'opacity-70' : ''} data-id="element-267" data-mp="1qvuvt812pi9k">
                      {action.activityProduct}
                    </Badge>
                  </td>
                  <td className="px-4 py-3" data-id="element-268" data-mp="1zuq36nfusyeg">
                    <div className="flex flex-col gap-1" data-id="element-269" data-mp="24vvuqhxs2fme">
                      <span className={`text-xs font-medium ${action.isDone ? 'text-slate-500' : 'text-seform-navy'}`} data-id="element-270" data-mp="24t9nykipo458">
                        {action.stepName}
                      </span>
                      <div className="flex items-center gap-2" data-id="element-271" data-mp="2g59v3mp2vxx">
                        <div className="w-full bg-slate-200 rounded-full h-1.5 max-w-[80px]" data-id="element-272" data-mp="3ukplf7zqlpvd">
                          <div className={`h-1.5 rounded-full ${action.isDone ? 'bg-slate-400' : 'bg-seform-orange'}`} style={{
                        width: `${action.stepProgress}%`
                      }} data-id="element-273" data-mp="3un28fzkcqqfa"></div>
                        </div>
                        <span className="text-xs text-slate-500" data-id="element-274" data-mp="hd4nb8489j0b">
                          {action.stepProgress}%
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3" data-id="element-275" data-mp="q717wihqundm">
                    <span className={`text-sm font-mono ${action.isDone ? 'text-slate-500' : isOverdue ? 'text-red-600 font-bold' : 'text-slate-600'}`} data-id="element-276" data-mp="153rzi5l6euso">
                      {formatDate(action.deadline)}
                    </span>
                  </td>
                  {showResponsible && <td className="px-4 py-3" data-id="element-277" data-mp="1nr0znkz7mtpy">
                      <span className={`text-sm ${action.isDone ? 'text-slate-500' : 'text-slate-700'}`} data-id="element-278" data-mp="1nppwnndl9pzw">
                        {action.responsible}
                      </span>
                    </td>}
                  <td className="px-4 py-3 text-right" data-id="element-279" data-mp="100wx7gxrd00j">
                    <div className="flex items-center justify-end gap-1" data-id="element-280" data-mp="14tq6mi468wiy">
                      <Tooltip content="Vedi dettagli" data-id="element-281" data-mp="r1s3cnx55uup">
                        <button onClick={e => handleView(e, action)} className="p-1.5 text-slate-500 hover:text-seform-navy rounded-md hover:bg-slate-100 transition-colors" data-id="element-282" data-mp="r3cxicfjnbs1">
                          <Eye className="h-4 w-4" data-id="element-283" data-mp="m2ffq0yg4jr2" />
                        </button>
                      </Tooltip>
                      {!action.isDone && <Tooltip content="Modifica" data-id="element-284" data-mp="11jx6g8re1h6a">
                          <button onClick={e => handleEdit(e, action)} className="p-1.5 text-seform-orange hover:text-seform-orange/80 rounded-md hover:bg-orange-50 transition-colors" data-id="element-285" data-mp="xiv6tqtk733s">
                            <Edit className="h-4 w-4" data-id="element-286" data-mp="ilufhofa5a5u" />
                          </button>
                        </Tooltip>}
                      <Tooltip content="Elimina" data-id="element-287" data-mp="ducdqy5fn5jt">
                        <button onClick={e => handleDelete(e, action.id)} className="p-1.5 text-red-500 hover:text-red-600 rounded-md hover:bg-red-50 transition-colors" data-id="element-288" data-mp="9tk4i4wk90nj">
                          <Trash2 className="h-4 w-4" data-id="element-289" data-mp="3vzfnwxa9a7r3" />
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>

      {/* View Modal */}
      <Modal isOpen={viewModalOpen} onClose={() => setViewModalOpen(false)} title="Dettaglio Azione" size="lg" data-id="element-290" data-mp="3lw0sv03lgri7">
        {selectedAction && <div className="space-y-6" data-id="element-291" data-mp="3vvjdzjv8qjbu">
            <div className="grid grid-cols-2 gap-4" data-id="element-292" data-mp="3vuhpodp7s0c6">
              <div data-id="element-293" data-mp="2y7hk6jgmucpl">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-294" data-mp="2y8alh4wtoa0c">
                  Nome Azione
                </span>
                <p className="font-medium text-slate-900" data-id="element-295" data-mp="336lujdfamghi">
                  {selectedAction.name}
                </p>
              </div>
              <div data-id="element-296" data-mp="3cvvpd11iud9b">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-297" data-mp="3cwdqoc31jho3">
                  Stato
                </span>
                <div className="mt-1" data-id="element-298" data-mp="2fg8b2barxpyj">
                  <Badge variant={selectedAction.isDone ? 'success' : 'warning'} data-id="element-299" data-mp="2kfun4hev905r">
                    {selectedAction.isDone ? 'Completata' : 'Da fare'}
                  </Badge>
                </div>
              </div>
              <div data-id="element-300" data-mp="uvuup3bjp0bw">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-301" data-mp="uzilvvtipang">
                  Assegnatario
                </span>
                <p className="font-medium text-slate-900 flex items-center gap-2 mt-1" data-id="element-302" data-mp="13ym8h9rw886p">
                  {selectedAction.assigneeType === 'consulente' ? <User className="h-4 w-4 text-seform-orange" data-id="element-303" data-mp="75qtf2ltxco2" /> : <Briefcase className="h-4 w-4 text-blue-500" data-id="element-304" data-mp="c76g0z3nnr8k" />}
                  {selectedAction.assigneeName} ({selectedAction.assigneeType})
                </p>
              </div>
              <div data-id="element-305" data-mp="l1cnho6x6mnc">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-306" data-mp="3kmq13au6a3cj">
                  Cliente
                </span>
                <p className="font-medium text-slate-900" data-id="element-307" data-mp="3onjps6ffvm6h">
                  {selectedAction.clientName}
                </p>
              </div>
              <div data-id="element-308" data-mp="3h2px5jq2jqyv">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-309" data-mp="3c5ppx5hhgi66">
                  Attività / Prodotto
                </span>
                <p className="font-medium text-slate-900" data-id="element-310" data-mp="3832rumu7y10j">
                  {selectedAction.activityProduct}
                </p>
              </div>
              <div data-id="element-311" data-mp="337dv0r9l2y7x">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-312" data-mp="2y6hp685o91zv">
                  Step
                </span>
                <p className="font-medium text-slate-900" data-id="element-313" data-mp="2te6mwyngl445">
                  {selectedAction.stepName} ({selectedAction.stepProgress}%)
                </p>
              </div>
              <div data-id="element-314" data-mp="2kf34lww48sqp">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-315" data-mp="2kfky4lre8sux">
                  Scadenza
                </span>
                <p className="font-medium text-slate-900" data-id="element-316" data-mp="2fhjg845pezl1">
                  {formatDate(selectedAction.deadline)}
                </p>
              </div>
              <div data-id="element-317" data-mp="20si1hwx2wq5a">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-318" data-mp="20rymanytcfuy">
                  Responsabile
                </span>
                <p className="font-medium text-slate-900" data-id="element-319" data-mp="25phyzjfv4kks">
                  {selectedAction.responsible}
                </p>
              </div>
            </div>
            <div className="flex justify-between items-center pt-4 border-t border-slate-100" data-id="element-320" data-mp="1vug9unll7ii7">
              <button onClick={() => {
            actionsStore.toggleDone(selectedAction.id);
            setSelectedAction({
              ...selectedAction,
              isDone: !selectedAction.isDone
            });
            if (onActionUpdate) onActionUpdate();
          }} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${selectedAction.isDone ? 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100' : 'bg-slate-50 text-slate-700 border border-slate-200 hover:bg-slate-100'}`} data-id="element-321" data-mp="2jkkacm3hb24z">
                {selectedAction.isDone ? <CheckCircle2 className="h-4 w-4 text-green-600" data-id="element-322" data-mp="qtqh2trdckch" /> : <Circle className="h-4 w-4 text-slate-400" data-id="element-323" data-mp="15jxqkjzeet11" />}
                {selectedAction.isDone ? 'Completata' : 'Segna come completata'}
              </button>
              <Button onClick={() => setViewModalOpen(false)} data-id="element-324" data-mp="cuqursrbwhjh">Chiudi</Button>
            </div>
          </div>}
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={editModalOpen} onClose={() => setEditModalOpen(false)} title="Modifica Azione" size="lg" data-id="element-325" data-mp="7xqjn3fmgoli">
        {selectedAction && <div className="space-y-6" data-id="element-326" data-mp="hsii09h13fhc">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-327" data-mp="1xau1vg5t8pm7">
              <Input label="Nome Azione" defaultValue={selectedAction.name} className="col-span-full" data-id="element-328" data-mp="1x90t9vbmzvbk" />
              <Input label="Assegnatario" defaultValue={selectedAction.assigneeName} data-id="element-329" data-mp="2ok70r5517v22" />
              <DatePicker label="Scadenza" value={selectedAction.deadline} onChange={() => {}} data-id="element-330" data-mp="2slaenm3irh0n" />
              <Textarea label="Note" placeholder="Inserisci eventuali note..." className="col-span-full" rows={3} data-id="element-331" data-mp="32jgfe0dlah3x" />
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-332" data-mp="3bitebz437nae">
              <Button variant="flat" onClick={() => setEditModalOpen(false)} data-id="element-333" data-mp="3ga35531dgwnv">
                Annulla
              </Button>
              <Button onClick={handleSaveEdit} data-id="element-334" data-mp="3l8czjbf9vt8f">Salva modifiche</Button>
            </div>
          </div>}
      </Modal>
    </>;
}